import { User } from "@shared/models";

export const copyObject = <T,>(obj: T) => JSON.parse(JSON.stringify(obj));

export const debounce = <F extends (...args: Parameters<F>) => ReturnType<F>>(func: F, waitFor = 500) => {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  const debounced = (...args: Parameters<F>) => {
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(() => func(...args), waitFor);
  };

  return debounced as (...args: Parameters<F>) => ReturnType<F>;
};

export const getUniqueElements = <T,>(array: T[], key: keyof T): T[] => {
  return Array.from(new Map(array.map((item) => [item[key], item])).values());
};

// D2 (923-core-context context/engineering/spark-rewards/phase-38-implementation-plan.md): widened from `User | null` to accept any narrower shape that
// has just first_name/last_name -- a leaderboard row's `user` ({ id, first_name, last_name }) does not
// structurally satisfy `User | null` as originally typed (missing User's other required fields).
// Backward-compatible: every existing `User`-typed call site still satisfies the narrower
// `Pick<User, "first_name" | "last_name">`, so this is zero behavior change for them.
export const getFullName = (user: Pick<User, "first_name" | "last_name"> | null) => {
  return user ? [user.first_name, user.last_name].filter((el) => Boolean(el)).join(" ") : "";
};

// DD-Mon-YYYY
export const getGbFormattedDate = (date: Date | string | undefined | null) => {
  return date
    ? new Date(date).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "-";
};

// Identical to getGbFormattedDate but with a SHORT month ("2 Sept 2026", not "2 September 2026") —
// overview-rebuild-pr3-plan.md "Date format": the long form doesn't fit a 130px badge-card caption at
// 10px. Added beside it rather than adding a `format` param to the existing function, since every
// other call site of getGbFormattedDate wants the long form and a param only one caller ever sets
// would be dead weight there.
export const getGbFormattedShortDate = (date: Date | string | undefined | null) => {
  return date
    ? new Date(date).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "-";
};

// 923-core-context context/engineering/spark-rewards/phase-56-drawer-follow-ups.md #4: a day+month-only sibling of getGbFormattedShortDate above -- "10
// Sept", never "10 Sept 2026" -- for the Person Sparks drawer's REDEEMED row meta line, whose design has
// no year at all (unlike every other date this family formats). Added beside it rather than adding a
// `year` toggle param to getGbFormattedShortDate itself, since that function's 3 other live callers
// (RecognitionListItem/BadgeCard/YourAllTimeCard) all keep the year and must not change. "Sept" (not
// "Sep") is this runtime's own en-GB ICU abbreviation and is what every other date in this app already
// shows (ledger "10 Sept, 14:32", badges "19 Sept 2026") -- kept for consistency, not shortened further.
export const getGbFormattedDayMonth = (date: Date | string | undefined | null) => {
  return date
    ? new Date(date).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
      })
    : "-";
};

// 923-core-context context/engineering/spark-rewards/phase-49-w3-pending-requests-panel.md: module-level singletons, matching formatMessageTime's own
// Intl.DateTimeFormat singleton below. TWO formatters, not one, because no single
// Intl.RelativeTimeFormat style renders both strings the design calls for: `style: "long"` (the
// default) spells minutes out ("12 minutes ago"), while `style: "short"`/`"narrow"` abbreviate hours
// too ("1 hr ago") -- verified against this runtime's actual en-GB ICU output, not assumed. So minutes
// get their own short-styled formatter and every larger unit uses the long-styled default.
const relativeTimeFormatter = new Intl.RelativeTimeFormat("en-GB", { numeric: "auto" });
const relativeMinutesFormatter = new Intl.RelativeTimeFormat("en-GB", { numeric: "auto", style: "short" });

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/**
 * Relative time phrasing for a past (or future) date/time -- "12 min ago", "1 hour ago", "yesterday",
 * "2 days ago" (923-core-context context/engineering/spark-rewards/phase-49-w3-pending-requests-panel.md's Requested column). `numeric: "auto"` on both
 * formatters is what yields "yesterday" rather than "1 day ago" for a date exactly one day off — the
 * design shows that idiom specifically.
 *
 * Follows this file's own "-"-for-nullish convention (`getGbFormattedDate` et al.) for a null/undefined/
 * invalid input, and its add-a-sibling rule — no new `format` param on `formatMessageTime` below.
 *
 * @param date - the timestamp to phrase relative to now; `null`/`undefined`/unparseable renders "-".
 * @returns the relative-time phrase, or "-" if `date` is absent or invalid.
 */
// fix pass J: an arrow-const, matching the newer date-formatting exports just above it
// (getGbFormattedDate/ShortDate/DateTime et al.) -- the original `export function formatRelativeTime`
// diverged from those, even though a few older exports further down this file still use `function`.
export const formatRelativeTime = (date: Date | string | undefined | null): string => {
  if (!date) return "-";
  const target = new Date(date);
  if (Number.isNaN(target.getTime())) return "-";

  const diffMs = target.getTime() - Date.now();

  const diffMinutes = Math.round(diffMs / MINUTE_MS);
  if (Math.abs(diffMinutes) < 60) {
    return relativeMinutesFormatter.format(diffMinutes, "minute");
  }

  const diffHours = Math.round(diffMs / HOUR_MS);
  if (Math.abs(diffHours) < 24) {
    return relativeTimeFormatter.format(diffHours, "hour");
  }

  const diffDays = Math.round(diffMs / DAY_MS);
  return relativeTimeFormatter.format(diffDays, "day");
};

/**
 * The Review tab's "Age" column (review-tab-redesign-plan.md, PR2): "12 min ago", "2 hours ago",
 * "Yesterday", "2 days ago". Delegates to `formatRelativeTime` above rather than re-deriving the same
 * minute/hour/day boundary math beside it — the plan's own claim that no relative-time formatter exists
 * predates `formatRelativeTime` (added for the pending-requests panel's "Requested" column), and the two
 * columns want the identical phrasing with ONE difference: `formatRelativeTime`'s own test
 * (function.test.tsx) pins lowercase "yesterday" for its caller, while this design capitalizes it. A
 * second boundary implementation would just be a second place for those boundaries to drift apart, so
 * `formatAge` only adds the capitalization `formatRelativeTime` deliberately doesn't do.
 *
 * Capitalizing unconditionally (not special-casing "yesterday") is safe for every other phrase this can
 * produce ("12 min ago", "2 hours ago", "2 days ago", "-") — each already starts with a digit or a
 * hyphen, neither of which `toUpperCase()` changes.
 *
 * @param date - the timestamp to phrase relative to now; `null`/`undefined`/unparseable renders "-".
 * @returns the relative-age phrase, capitalized, or "-" if `date` is absent or invalid.
 */
export const formatAge = (date: Date | string | undefined | null): string => {
  const relative = formatRelativeTime(date);
  return relative.charAt(0).toUpperCase() + relative.slice(1);
};

export const getGbFormattedDateTime = (date: Date | string | undefined | null) => {
  return date
    ? new Date(date).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "numeric",
        minute: "numeric",
        hour12: true,
      })
    : "-";
};

// phase-50-w6b: a SHORT sibling of getGbFormattedDateTime above -- "10 Sept, 14:32" vs that function's
// "10 September 2026 at 2:32 pm" -- for the org-wide redemption ledger's "When" column, which the design
// frame renders noticeably tighter (comment on orgWideRedemptionLedgerTableProperties.tsx's own "When"
// column has the history: an earlier phase (W5) deliberately reused getGbFormattedDateTime rather than
// add a format for a single caller; this phase's plan explicitly asks for the short form, so this file's
// existing date-format FAMILY gains a fourth sibling instead of a one-off formatter living elsewhere).
// No year (this column is never far from "now"), 24-hour (no am/pm suffix in the design), day+month
// only. The comma `toLocaleDateString` inserts between the date and time parts is stripped to match the
// design's plain "day month time" shape.
export const getGbFormattedShortDateTime = (date: Date | string | undefined | null) => {
  return date
    ? new Date(date)
        .toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        })
        .replace(",", "")
    : "-";
};

export const getRecurrenceDaysString = (days: number | undefined | null) => {
  return days ? (days === 180 ? "Every 6 months" : `Every ${days} days`) : "-";
};

export const calculateDatesDiff = (
  date1: Date | string | undefined | null,
  date2: Date | string | undefined | null,
) => {
  if (!date1 || !date2) {
    return "-";
  }
  const timeDiff = Math.abs(new Date(date1).getTime() - new Date(date2).getTime());
  const diffSeconds = Math.ceil(timeDiff / 1000);

  return diffSeconds >= 60 ? `${Math.round(timeDiff / (1000 * 60))} minutes` : `${diffSeconds} seconds`;
};

export function getCutString(str: string, maxLength = 103, dotted = true) {
  return str.length > maxLength
    ? dotted
      ? str.substring(0, maxLength - 3) + "..."
      : str.substring(0, maxLength)
    : str;
}

export function openInNewTab(url: string) {
  window.open(url);
}

export function downLoadFile(data: any, type: string, name: string) {
  const blob = new Blob([data], { type: type });
  const url = window.URL.createObjectURL(blob);

  const anchor = document.createElement("a");
  anchor.download = `${name}.csv`;
  anchor.href = url;
  anchor.click();
}

/**
 * Writes `text` to the clipboard, resolving to whether the write actually succeeded -- never
 * rejects, so a caller that doesn't care about the outcome can still call it fire-and-forget.
 * Chat's copy button (design-spec.md §3 Chat/MessageActions "copied" state, tracker bug-03) uses the
 * resolved value to show its check only on a real success, never on a rejected clipboard write.
 *
 * @param text - the text to copy.
 * @returns a promise resolving true on success, false on a rejected clipboard write.
 */
export function copyToClipboard(text: string): Promise<boolean> {
  return navigator.clipboard.writeText(text).then(
    () => true,
    () => false,
  );
}

const messageTimeFormatter = new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit", hour12: true });

export function formatMessageTime(isoString?: string): string {
  if (!isoString) return "";
  const date = new Date(isoString);
  return Number.isNaN(date.getTime()) ? "" : messageTimeFormatter.format(date);
}

/** Router `state` shape ProcedureListContainer/UserProceduresContainer attach when navigating to a
 *  procedure's details page (`navigate(path, { state: { returnTo } })`), read back by
 *  ProcedureDetails.tsx's backBtnHandler via `isProcedureReturnState` below. Carried on the history
 *  entry itself (not persisted storage), so it's naturally scoped to that one navigation and never
 *  goes stale or outlives a sign-out. */
export interface ProcedureReturnState {
  returnTo: string;
}

// Same-app path prefixes backBtnHandler is willing to return to -- anything else (a foreign `state`
// shape, or a `state` object left over from an unrelated history entry react-router happens to reuse)
// is rejected so "Back to List" falls back to its own default instead of navigating off the
// procedures/users area.
const PROCEDURE_RETURN_TO_PREFIXES = ["/procedures", "/users/"];

/**
 * Type guard for `ProcedureReturnState` -- narrows `useLocation().state` (typed `unknown` by
 * react-router) so ProcedureDetails.tsx's backBtnHandler can read `returnTo` without a cast. Only a
 * `returnTo` starting with an allowed same-app prefix is accepted.
 *
 * @param state - `useLocation().state`.
 * @returns true when `state` is a `ProcedureReturnState` with an allowed `returnTo` prefix.
 */
export function isProcedureReturnState(state: unknown): state is ProcedureReturnState {
  if (typeof state !== "object" || state === null) return false;

  const { returnTo } = state as Partial<ProcedureReturnState>;
  return typeof returnTo === "string" && PROCEDURE_RETURN_TO_PREFIXES.some((prefix) => returnTo.startsWith(prefix));
}
